import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useState } from 'react'

const PHONE = '405-848-5801'

// Base for the public/marketing pages that live on the Wix site. The portal
// mirrors the Wix top-nav so the two sites read as one. Repoint WIX_BASE to
// https://pigeon.org once the Wix site is live on the primary domain.
const WIX_BASE = 'https://www.pigeon-test.org'
const NDB = 'https://pigeon-ndb.com'

type MenuChild = { label: string; href: string; internal?: boolean }
type MenuItem = { label: string; href?: string; internal?: boolean; children?: MenuChild[] }

// Public nav — mirrors the Wix menu (content links -> Wix pages; member-facing
// actions -> the portal).
const PUBLIC_MENU: MenuItem[] = [
  { label: 'Home', href: WIX_BASE + '/' },
  { label: 'About', children: [
    { label: 'History', href: WIX_BASE + '/general-8' },
    { label: 'Board of Directors', href: WIX_BASE + '/general-6' },
    { label: 'Financials', href: WIX_BASE + '/general-5-1' },
  ] },
  { label: 'Report Lost Pigeon', href: WIX_BASE + '/general-4' },
  { label: 'Racing', children: [
    { label: 'Rules and Regulations', href: WIX_BASE + '/category/bird-food' },
    { label: 'By-Laws', href: WIX_BASE + '/category/bird-houses' },
    { label: 'Policies', href: WIX_BASE + '/category/bird-toys' },
    { label: 'Forms', href: WIX_BASE + '/general-5-2' },
  ] },
  { label: 'Members Services', children: [
    { label: 'Log In', href: '/login', internal: true },
    { label: 'Become a Member', href: '/join', internal: true },
    { label: 'Pay Dues', href: '/dues', internal: true },
    { label: 'Order Bands', href: '/bands', internal: true },
    { label: 'Band Listings', href: '/band-listings', internal: true },
  ] },
  { label: 'Sports Promotion', children: [
    { label: 'Events', href: WIX_BASE + '/copy-of-race-secretary-1' },
  ] },
  { label: 'National Database', href: NDB + '/' },
  { label: 'Club', children: [
    { label: 'Race Secretary', href: WIX_BASE + '/general-5' },
    { label: 'Secretary', href: WIX_BASE + '/copy-of-race-secretary-2' },
    { label: 'Club Directory', href: '/clubs', internal: true },
    { label: 'Find a Club', href: '/find-club', internal: true },
    { label: 'Start a Club', href: WIX_BASE + '/copy-of-find-club' },
    { label: 'Beginners', href: WIX_BASE + '/copy-of-club-web-page-portal' },
    { label: 'Loft Certification Program', href: WIX_BASE + '/copy-of-beginners' },
  ] },
]

const MEMBER_MENU: MenuItem[] = [
  { label: 'Dashboard', href: '/dashboard', internal: true },
  { label: 'Pay Dues', href: '/dues', internal: true },
  { label: 'Order Bands', href: '/bands', internal: true },
  { label: 'My Races', href: '/races', internal: true },
  { label: 'Profile', href: '/profile', internal: true },
  { label: 'Clubs', href: '/clubs', internal: true },
]

export function Header() {
  const { user, isAuthenticated, logout } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // Club secretaries get a direct link to their club page editor. One club
  // links straight to the editor; multiple clubs become a dropdown.
  const secretaryClubs = user?.secretaryClubs ?? []
  const memberMenu: MenuItem[] =
    secretaryClubs.length === 0
      ? MEMBER_MENU
      : [
          ...MEMBER_MENU,
          secretaryClubs.length === 1
            ? { label: 'Edit Club Page', href: `/clubs/${secretaryClubs[0].slug}/edit`, internal: true }
            : {
                label: 'Edit Club Page',
                children: secretaryClubs.map((c) => ({
                  label: c.clubName,
                  href: `/clubs/${c.slug}/edit`,
                  internal: true,
                })),
              },
        ]

  const menu = isAuthenticated ? memberMenu : PUBLIC_MENU
  const isActive = (href?: string, internal?: boolean) =>
    !!href && internal === true && (pathname === href || (href !== '/' && pathname.startsWith(href)))

  const handleSearch = (e: { preventDefault: () => void }) => {
    e.preventDefault()
    const q = search.trim()
    navigate(q ? `/clubs?q=${encodeURIComponent(q)}` : '/clubs')
    setMobileMenuOpen(false)
  }

  const childLink = (c: MenuChild, onClick?: () => void) =>
    c.internal ? (
      <Link key={c.label} to={c.href} onClick={onClick} className="block px-4 py-2 text-[13px] text-text hover:text-secondary hover:bg-surface-alt">
        {c.label}
      </Link>
    ) : (
      <a key={c.label} href={c.href} onClick={onClick} className="block px-4 py-2 text-[13px] text-text hover:text-secondary hover:bg-surface-alt">
        {c.label}
      </a>
    )

  const topLink = (item: MenuItem) => {
    const cls = `font-heading font-bold text-[13px] transition-colors ${
      isActive(item.href, item.internal) ? 'text-secondary' : 'text-text hover:text-secondary'
    }`
    if (item.internal) return <Link to={item.href!} className={cls}>{item.label}</Link>
    return <a href={item.href!} className={cls} target={item.href!.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">{item.label}</a>
  }

  return (
    <header className="font-body">
      {/* Crimson top strip */}
      <div className="h-2.5 w-full bg-secondary" />

      <div className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-page">
          <div className="flex items-center justify-between gap-4 py-3">
            <Link to="/" className="flex shrink-0 items-center" aria-label="ARPU home">
              <img src="/arpu-logo.png" alt="American Racing Pigeon Union" className="h-14 w-auto" />
            </Link>

            {/* Desktop nav — mirrors the Wix menu (with dropdowns) */}
            <nav className="hidden lg:flex items-center gap-6">
              {menu.map((item) =>
                item.children ? (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(item.label)}
                    onMouseLeave={() => setOpenDropdown((d) => (d === item.label ? null : d))}
                  >
                    <button className="font-heading font-bold text-[13px] text-text hover:text-secondary transition-colors flex items-center gap-1">
                      {item.label}
                      <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M3 4.5L6 7.5L9 4.5" /></svg>
                    </button>
                    {openDropdown === item.label && (
                      <div className="absolute left-0 top-full pt-2 z-20">
                        <div className="min-w-[210px] bg-surface border border-border shadow-md py-1">
                          {item.children.map((c) => childLink(c, () => setOpenDropdown(null)))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <span key={item.label}>{topLink(item)}</span>
                ),
              )}
            </nav>

            {/* Right cluster: auth + Call Us */}
            <div className="hidden lg:flex items-center gap-4 shrink-0">
              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  <span className="text-[13px] text-text-muted">{user?.firstName} {user?.lastName}</span>
                  <button onClick={logout} className="font-heading font-bold text-[13px] text-text hover:text-secondary transition-colors">Logout</button>
                </div>
              ) : (
                <Link to="/login" className="flex items-center gap-1.5 font-heading font-bold text-[13px] text-text hover:text-secondary transition-colors">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                    <circle cx="12" cy="8" r="3.2" />
                    <path strokeLinecap="round" d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" />
                  </svg>
                  Log In
                </Link>
              )}
              <a href={`tel:${PHONE}`} className="bg-callus text-text text-center leading-tight px-5 py-2 hover:brightness-95 transition">
                <span className="block font-heading font-semibold text-[17px]">Call Us</span>
                <span className="block text-sm tracking-wide">{PHONE}</span>
              </a>
            </div>

            {/* Mobile hamburger */}
            <button className="lg:hidden p-2 text-text" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                {mobileMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Search band (desktop) */}
      <div className="hidden lg:block bg-[#f9f9f9] border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-page py-2 flex justify-end">
          <form onSubmit={handleSearch} className="flex w-72">
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clubs..." aria-label="Search clubs"
              className="flex-1 border border-border bg-surface px-3 py-1.5 text-sm text-text focus:outline-none focus:border-primary" />
            <button type="submit" aria-label="Search" className="bg-primary-dark text-text-inverse px-3 flex items-center justify-center hover:bg-primary transition-colors">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path strokeLinecap="round" d="M21 21l-4.3-4.3" /></svg>
            </button>
          </form>
        </div>
      </div>

      {/* Mobile menu — flattened (top items + their children) */}
      {mobileMenuOpen && (
        <nav className="lg:hidden bg-surface border-b border-border px-page py-4 space-y-2 font-body text-text">
          {menu.map((item) => (
            <div key={item.label}>
              {item.children ? (
                <>
                  <p className="font-heading font-bold text-[13px] uppercase tracking-wide text-text-muted mt-2">{item.label}</p>
                  {item.children.map((c) => childLink(c, () => setMobileMenuOpen(false)))}
                </>
              ) : item.internal ? (
                <Link to={item.href!} onClick={() => setMobileMenuOpen(false)} className="block py-1 font-heading font-bold text-[13px] hover:text-secondary">{item.label}</Link>
              ) : (
                <a href={item.href!} onClick={() => setMobileMenuOpen(false)} target={item.href!.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="block py-1 font-heading font-bold text-[13px] hover:text-secondary">{item.label}</a>
              )}
            </div>
          ))}
          {isAuthenticated ? (
            <button onClick={() => { logout(); setMobileMenuOpen(false) }} className="block py-1 font-heading font-bold text-[13px] hover:text-secondary">Logout</button>
          ) : (
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-1 font-heading font-bold text-[13px] hover:text-secondary">Log In</Link>
          )}
          <form onSubmit={handleSearch} className="flex pt-2">
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clubs..." aria-label="Search clubs"
              className="flex-1 border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:border-primary" />
            <button type="submit" aria-label="Search" className="bg-primary-dark text-text-inverse px-4 flex items-center justify-center">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path strokeLinecap="round" d="M21 21l-4.3-4.3" /></svg>
            </button>
          </form>
          <a href={`tel:${PHONE}`} className="block pt-1 text-primary">Call Us: {PHONE}</a>
        </nav>
      )}
    </header>
  )
}
